import type { Abilities, AnyAbility, CanParameters, RawRule } from '@casl/ability'
import { appError } from '@pkg/shared/errors'

export interface CanResult {
  can: boolean
  reason?: string
  rule?: RawRule<any, any> | null
}

export const can = <TAbility extends AnyAbility, TAbilities extends Abilities>(
  ability: TAbility,
  action: CanParameters<TAbilities>[0],
  subject: CanParameters<TAbilities>[1],
  field?: string,
  _debug?: boolean,
): CanResult => {
  const rule = ability.relevantRuleFor(action, subject as never, field)
  const can = !!rule && !rule.inverted
  return { can, reason: rule?.reason, rule }
}

export const canFactory =
  <TAbility extends AnyAbility, TAbilities extends Abilities>(ability: TAbility) =>
  (
    action: CanParameters<TAbilities>[0],
    subject: CanParameters<TAbilities>[1],
    field?: string,
    debug?: boolean,
  ): CanResult =>
    can(ability, action, subject, field, debug)

export const assertCan = <TAbility extends AnyAbility, TAbilities extends Abilities>(
  ability: TAbility,
  action: CanParameters<TAbilities>[0],
  subject: CanParameters<TAbilities>[1],
  field?: string,
  buildAppError?: (reason?: string) => unknown,
  debug?: boolean,
) => {
  const result = can(ability, action, subject, field, debug)
  if (!result.can) {
    if (buildAppError) {
      throw buildAppError(result.rule?.reason)
    }
    throw appError('NO_PERMISSION' as never, {
      data: { reason: result?.reason, rule: result?.rule },
    })
  }
}

export const assertCanFactory =
  <TAbility extends AnyAbility, TAbilities extends Abilities>(
    ability: TAbility,
    buildGeneralError?: (reason?: string) => unknown,
  ) =>
  (
    action: CanParameters<TAbilities>[0],
    subject: CanParameters<TAbilities>[1],
    field?: string,
    buildAppError?: (reason?: string) => unknown,
    debug?: boolean,
  ) => {
    assertCan(ability, action, subject, field, buildAppError ?? buildGeneralError, debug)
  }
